// Shared types + helpers for the case-management surfaces.
import { formatPortDisplay } from '@/lib/ports'

export type CaseStatus = 'draft' | 'intake' | 'review' | 'ready' | 'transmitted' | 'archived'

export type Case = {
  id: string
  case_number: string
  agent_id: string
  assigned_agent_name?: string | null
  assigned_agent_email?: string | null
  status: CaseStatus
  title: string
  summary: string | null
  importer_of_record: string | null
  port_of_entry: string | null
  total_value_usd: number | null
  total_duty_usd: number | null
  flagged_count: number
  transmitted_at: string | null
  archived_at: string | null
  created_at: string
  updated_at: string
}

export type StructuredCaseFieldValue = {
  value?: string | null
  displayValue?: string | null
}

export type CaseListStructuredSnapshot = {
  updatedAt: string | null
  fields?: Record<string, StructuredCaseFieldValue> | null
}

export type CaseListDisplayValues = {
  importer: string | null
  port: string | null
  value: number | null
  updatedAt: string
}

export type IntakeDocument = {
  id: string
  case_id: string
  storage_path: string
  original_filename: string
  mime_type: string
  size_bytes: number
  ocr_status: 'pending' | 'processing' | 'done' | 'error'
  ocr_text: string | null
  ocr_confidence: number | null
  uploaded_at: string
}

export type IntakeDocumentExtraction = {
  id: string
  intake_document_id: string
  case_id: string
  detected_document_type: string | null
  classification_method: string
  extracted_fields: Record<string, string | number | null>
  extracted_lines: Array<Record<string, unknown>>
  extraction_confidence: number | null
  created_at: string
  updated_at: string
}

export type EntryField = {
  id: string
  case_id: string
  field_key: string
  field_value: string | null
  source: 'ocr' | 'agent' | 'rule'
  confidence: number | null
  flagged: boolean
  flag_reason: string | null
  updated_at: string
}

export type EntryLine = {
  id: string
  case_id: string
  line_no: number
  hts_code: string | null
  description: string | null
  country_of_origin: string | null
  quantity: number | null
  unit_value: number | null
  line_total: number | null
  ad_valorem_rate: number | null
  duty_amount: number | null
  flagged: boolean
  flag_reason: string | null
  created_at: string
}

export type Transmission = {
  id: string
  case_id: string
  payload_json: unknown
  masked_fields: string[]
  status: 'queued' | 'sent' | 'error'
  ace_receipt_id: string | null
  sent_at: string | null
  error_message: string | null
  created_at: string
}

export type CaseEvent = {
  id: string
  case_id: string
  actor_id: string | null
  kind: string
  payload: Record<string, unknown>
  occurred_at: string
}

export const STATUS_LABELS: Record<CaseStatus, string> = {
  draft: 'New',
  intake: 'Intake',
  review: 'In Review',
  ready: 'Ready to Transmit',
  transmitted: 'Transmitted',
  archived: 'Archived',
}

export const STATUS_ORDER: CaseStatus[] = [
  'draft',
  'intake',
  'review',
  'ready',
  'transmitted',
  'archived',
]

export const FIELD_LABELS: Record<string, string> = {
  importer_of_record: 'Importer of record',
  importer_irs_no: 'Importer IRS #',
  consignee: 'Consignee',
  port_of_entry: 'Port of entry',
  entry_type: 'Entry type',
  manifest_number: 'Manifest #',
  country_of_origin: 'Country of origin',
  mode_of_transport: 'Mode of transport',
  vessel_name: 'Vessel / aircraft',
  bill_of_lading: 'Bill of lading',
  estimated_arrival: 'Estimated arrival',
  entered_value_usd: 'Entered value (USD)',
  estimated_duty_usd: 'Estimated duty (USD)',
}

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

export function formatUSD(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(amount))
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getStructuredFieldText(
  snapshot: CaseListStructuredSnapshot | null | undefined,
  fieldKey: string,
): string | null {
  if (!snapshot?.fields) return null
  const field = snapshot.fields[fieldKey]
  if (!field) return null
  return field.displayValue ?? field.value ?? null
}

function parseCurrencyLikeValue(value: string | null | undefined): number | null {
  if (!value) return null
  const normalized = value.replace(/[^0-9.-]/g, '')
  const numeric = Number(normalized)
  return Number.isFinite(numeric) ? numeric : null
}

export function deriveCaseListDisplayValues(
  kase: Case,
  snapshot?: CaseListStructuredSnapshot | null,
): CaseListDisplayValues {
  const importer = getStructuredFieldText(snapshot, 'importer_of_record') || kase.importer_of_record || null
  const port = formatPortDisplay(getStructuredFieldText(snapshot, 'port_of_entry') || kase.port_of_entry || null)
  const value =
    parseCurrencyLikeValue(getStructuredFieldText(snapshot, 'total_entered_value'))
    ?? kase.total_value_usd
    ?? null

  const caseUpdatedAt = new Date(kase.updated_at)
  const snapshotUpdatedAt = snapshot?.updatedAt ? new Date(snapshot.updatedAt) : null
  const updatedAt =
    snapshotUpdatedAt && Number.isFinite(snapshotUpdatedAt.getTime()) && snapshotUpdatedAt > caseUpdatedAt
      ? snapshot.updatedAt!
      : kase.updated_at

  return {
    importer,
    port,
    value,
    updatedAt,
  }
}

export function applyLatestAssignmentEvent(
  kase: Case | null | undefined,
  events: CaseEvent[] | null | undefined,
): Case | null {
  if (!kase) return null
  const latestAssignmentEvent = events?.find((event) => event.kind === 'case.assignment_updated')
  if (!latestAssignmentEvent) return kase

  const payload = latestAssignmentEvent.payload ?? {}
  const nextEmail =
    typeof payload.next_email === 'string' && payload.next_email.trim().length > 0
      ? payload.next_email.trim()
      : null
  const nextName =
    typeof payload.next_value === 'string' && payload.next_value !== 'Unassigned'
      ? payload.next_value
      : null
  const nextStatus =
    payload.next_status === 'draft'
    || payload.next_status === 'intake'
    || payload.next_status === 'review'
    || payload.next_status === 'ready'
    || payload.next_status === 'transmitted'
    || payload.next_status === 'archived'
      ? payload.next_status
      : kase.status

  return {
    ...kase,
    assigned_agent_email: nextEmail,
    assigned_agent_name: nextName,
    status: nextStatus,
    updated_at:
      latestAssignmentEvent.occurred_at && latestAssignmentEvent.occurred_at > kase.updated_at
        ? latestAssignmentEvent.occurred_at
        : kase.updated_at,
  }
}
